<template>
  <q-item
    class="q-px-sm q-py-sm items-center"
    clickable
    @click="$emit('select')"
  >
    <q-item-section>
      <q-item-section v-if="!$consumer">
        <q-item-label
          overline
          style="font-size: 10px"
        >
          {{ item.breadcrumbs }}
        </q-item-label>
      </q-item-section>
      <q-item class="q-pa-none q-pt-xs items-center">
        <q-item-section
          v-if="item.imageUrl"
          class="col-auto"
        >
          <q-avatar square>
            <div>
              <img
                class="full-width"
                :src="item.imageUrl"
              >
            </div>
          </q-avatar>
        </q-item-section>
        <q-item-label
          v-else
        >
          <q-icon
            :name="item.icon || 'settings'"
            size="md"
            class="q-pr-sm"
          />
        </q-item-label>

        <q-item-section>
          <q-item-section class="q-mb-xs">
            <q-item-label style="font-size: 18px;">
              {{ item.label }}
            </q-item-label>
          </q-item-section>
          <q-item-label v-if="$consumer && (item.model && item.model.typeName === 'Movie')">
            <q-chip
              v-if="item.model.future && item.model.dateOfFirstShowing"
              color="secondary"
              text-color="white"
              dense
            >
              Starts {{ $d(item.model.dateOfFirstShowing, 'mediumDate') }}
            </q-chip>
            <q-chip
              v-else-if="item.model.dateOfFirstShowing"
              color="primary"
              text-color="white"
              dense
            >
              Now Playing
            </q-chip>
            <q-chip
              v-else
              color="info"
              text-color="white"
              dense
            >
              TBC
            </q-chip>
          </q-item-label>
          <q-item-label
            v-if="item.description"
            caption
          >
            {{ item.description }}
          </q-item-label>
        </q-item-section>
      </q-item>
    </q-item-section>
  </q-item>
</template>

<script>
import { isConsumer } from 'src/config'
import Model from 'src/models/model'

function asHumanTypeName (fieldName) {
  if (_.endsWith(fieldName, 'ses') || _.endsWith(fieldName, 'xes')) {
    return _.startCase(fieldName.slice(0, -2))
  } else {
    return _.startCase(fieldName.slice(0, -1))
  }
}
function breadcrumbs (route) {
  if (!route) return null

  const routeElements = route.split('/')
  return _(routeElements).map((slug, index) => {
    if (slug === '') return null

    if (index === routeElements.length - 1) return null

    return _.startCase(slug)
  }).compact().value().join(' > ')
}

const defaultOptions = {
  icon: 'settings',
  iconField: 'icon',
  labelField: 'name',
  descriptionField: null,
  searchFields: ['name'],
  routePrefix: 'settings/site',
  imageUrlField: null,
  sortBy: null,
  Model: null,
  desc: true,
  variables: {}
}

export function createModelSearchFunction (fieldName, options) {
  options = { ...defaultOptions, ...options }
  const variables = options.variables

  const fieldNameForMatch = asHumanTypeName(fieldName).toLowerCase()
  const fieldNameComponents = (fieldNameForMatch + ' ' + _.startCase(fieldName).toLowerCase()).split(' ')

  let initialList
  async function loadInitialList (ModelClass, localOnly) {
    if (ModelClass && localOnly) {
      // Do nothing
      return []
    } else if (ModelClass) {
      const modelOptions = {
        queryName: fieldName,
        isCollection: true,
        variables: {
          ...variables,
          limit: 200
        }
      }

      _.each(options.relations, related => {
        modelOptions[related] = true
      })

      const model = new options.Model({}, modelOptions)

      await model.fetch()

      initialList = _(model.data)
    } else if (Model.configuration[fieldName]) {
      initialList = _(Model.configuration[fieldName].data)
    } else {
      throw Error('Unhandled search type model type: ' + fieldName)
    }
    if (options.sortBy) {
      initialList.sortBy(options.sortBy)
    }
    if (options.desc === false) {
      initialList.reverse()
    }
    initialList = initialList.value()
  }

  const fieldNameRouteComponent = _.kebabCase(fieldName)
  const routePrefix = `/${options.routePrefix}/${fieldNameRouteComponent}`

  function formatItem (item) {
    let route
    if (isConsumer) {
      if (item.typeName === 'Movie') {
        route = item.pagePath
      } else if (item.typeName === 'Page') {
        route = `/${item.slug}`
      } else if (item.typeName === 'NewsItem') {
        route = item.url
      } else {
        throw new Error(`Unhandled Search Item Type: ${item.typeName}`)
      }
    } else {
      route = `${routePrefix}/${item.id}`
    }

    let description
    if (options.descriptionField && _.isString(options.descriptionField)) {
      description = item[options.descriptionField]
    } else if (options.descriptionField && _.isFunction(options.descriptionField)) {
      description = options.descriptionField(item)
    }

    let icon
    if (options.iconField) {
      icon = item[options.iconField] || options.icon
    } else {
      icon = options.icon
    }

    let imageUrl
    if (options.imageField && _.isString(options.imageField)) {
      imageUrl = item[options.imageField]
    } else if (options.imageField && _.isFunction(options.imageField)) {
      imageUrl = options.imageField(item)
    }

    return {
      route,
      id: route,
      type: asHumanTypeName(fieldName),
      model: item,
      label: item[options.labelField],
      imageUrl,
      icon,
      description,
      breadcrumbs: breadcrumbs(route),
      component: 'model-search-component'
    }
  }
  if (options.Model?.paginate) {
    return async function (searchTerms, count, localOnly) {
      if (localOnly) return []
      if (options.Model && !options.Model.isViewPermitted) return []

      if (options.keyword && !searchTerms.join(' ').includes(options.keyword)) return []

      // Exclude the terms related to this type of item
      const modelSearchTerms = _.filter(searchTerms, term => {
        return !_.some(fieldNameComponents, component => component.startsWith(term))
      })

      const modelOptions = {
        queryName: fieldName,
        isCollection: true,
        movie: true,
        variables: {
          ...variables,
          noCount: true,
          searchString: modelSearchTerms.join(' '),
          limit: count
        }
      }

      _.each(options.relations, related => {
        modelOptions[related] = true
      })

      const model = new options.Model({}, modelOptions)

      await model.fetch()

      return _.map(model.data, formatItem)
    }
  } else {
    return function (searchTerms, count, localOnly) {
      if (options.Model && !options.Model.isViewPermitted) return []

      if (!initialList) loadInitialList(options.Model, localOnly)

      if (searchTerms.length === 0) return _(initialList).filter('isViewPermitted').take(count).value()

      const matchingItems = _(initialList).filter(item => {
        if (!item.isViewPermitted) return false

        return _.every(searchTerms, term => {
          if (routePrefix.includes(term)) return true

          return _.some(options.searchFields, field => {
            if (field === 'itemVariationName') {
              return _.some(item.itemVariations.data, itemVariation => itemVariation.name.toLowerCase().includes(term))
            } else if (field === 'itemModifierName') {
              return _.some(item.allModifiers, modifier => modifier.name.toLowerCase().includes(term))
            } else if (field === 'itemClass' && item.itemClass) {
              return item.itemClass.name.toLowerCase().includes(term)
            } else if (field === 'category' && item.category) {
              return item.category.name.toLowerCase().includes(term)
            } else if (!item[field]) {
              return false
            } else {
              return item[field].toLowerCase().includes(term)
            }
          })
        })
      }).take(count).value()

      return _.map(matchingItems, formatItem)
    }
  }
}

export default {
  name: 'ModelSearchComponent',
  props: {
    item: {
      type: Object,
      required: true
    },
    searchTerms: {
      type: Array,
      required: true
    }
  },
  emits: ['select']
}
</script>
